import type { BookingCreationSource } from '@/entities/bookings/booking-creation-source.enum';
import type { BookingStatus } from '@/entities/bookings/booking-status.enum';
import type { PaginationParams } from '@/entities/pagination/pagination-params';
import type { User } from '../auth/user.entity';
import type { Car } from '../cars/car.entity';
import type { Damage } from '../damage.entity';
import type { Station } from '../station/station.entity';
import type { LedgerEntry } from '../ledger/ledger-entry.entity';
import type { BookingLocationType } from './booking-location-type.enum';
import type { FindAllBookingsSort } from './find-all-bookings-sort.enum';
import type { RentalPartner } from './rental-partner.enum';
import type { CustomerData } from './customer-data';
import type { CustomerDataDto } from './customer-data.dto';
import type { CreditCardDetails } from './credit-card-details';
import type { Local } from '@/entities/auth/local.enum';
import {
  type AdditionalCostsDto,
  type BookingAllocationDto,
  type BookingBalanceDto,
  type BookingFlags,
  type BookingLedgerCostBreakdownDto,
  type BookingPricingDto,
  CancellationFee,
  CancelledBy,
  type CarSwapDto,
  type FraudDetectionInfo,
  type LocationGeocodedAddress,
  type SalesChannel,
} from '@carvia/ros-client-types';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import type { InvoicingType } from './invoicing-type.enum';
import type { Coupon } from '../coupons/coupon.entity';
import type { TaxProcedure } from '@carvia/ros-client-types';

export interface SchufaResult {
  scoreRange: string | null;
  scoreValue: number | null;
  scoreInfoText: string;
}

export interface Booking {
  id: string;
  bookingNumber: number;
  startDate: string;
  handoverDate: string;
  endDate: string;
  returnDate: string;
  customer?: User;
  customerData?: CustomerData;
  /** Resolved UI locale from the API (station + preferred language). */
  displayLocale: Local;
  bookedCar: Car;
  allocations: Allocation[];
  pickupLocationType: BookingLocationType;
  dropoffLocationType: BookingLocationType;
  pickupStation: Station | null;
  pickupLocationGeocodedAddress?: LocationGeocodedName | null;
  dropoffStation: Station | null;
  pickupLocation: LocationPoint | null;
  dropoffLocation: LocationPoint | null;
  dropoffLocationGeocodedAddress?: LocationGeocodedName | null;
  status: BookingStatus;
  damages: Damage[] | null;
  blockedCreditCardAmount: number | null;
  blockedCreditCardDate: string | null;
  blockedCreditCardRefNr: string | null;
  blockedCreditCardIncludesOpenFee: boolean | null;
  depositPaymentLinkUrl: string | null;
  flags: BookingFlags;
  priceCalculation: Pricing;
  customerComment: string | null;
  confirmationReply: string | null;
  externalReference: string | null;
  internalComment: string | null;
  schufaResult: SchufaResult | null;
  createdDate: string;
  updatedDate: string;
  customInclusiveKm: number | null;
  totalRentalDays: number;
  additionalDrivers: AdditionalDriver[];
  contractFileKey: string | null;
  contractFileUrl?: string;
  confirmationFileUrl?: string;
  totalMilage: number | null;
  totalMilageExceeded: number | null;
  totalDurationDays: number;
  totalDurationExceeded: number;
  currentBalance: BookingBalanceDto;
  ledgerCostBreakdown: BookingLedgerCostBreakdownDto;
  isReadyToFinalize: boolean;
  agency: User | null;
  createdBy?: User | null;
  creationSource: BookingCreationSource | null;
  salesChannel: SalesChannel | null;
  hasBlockedCreditCard: boolean;
  hasOnlineCreditCardPayment: boolean;
  blockedCreditCardDetails: CreditCardDetails | null;
  onlineCreditCardDetails: CreditCardDetails | null;
  hasUnhandledDamages: boolean;
  hasPeriodicInvoicing: boolean;
  taxProcedure: TaxProcedure;
  collectDeposit?: boolean;
  unpaidDeposit?: number;
  debitCardPaymentAtHandover: boolean;
  deferredPayment: boolean;
  insuranceCase?: InsuranceCase | null;
  calculatedAdditionalCosts?: AdditionalCostsDto | null;
  carSwaps: CarSwapDto[];
  invoicingType: InvoicingType;
  bookingIpInfo: Record<string, any> | null;
  ledgerEntries?: LedgerEntry[];
  fraudDetectionInfo?: FraudDetectionInfo | null;
  coupons?: Coupon[];
}

export interface InsuranceCase {
  id: string;
  customerData: CustomerData;
  dropoffLocationGeocodedAddress: LocationGeocodedAddress | null;
  endDate: string;
  insuranceData: {
    [key: string]: unknown;
  } | null;
  insuranceProvider: string;
  pickupLocationGeocodedAddress: LocationGeocodedAddress | null;
  startDate: string;
  status: 'INITIAL' | 'BOOKED' | 'HANDED_OVER' | 'RETURNED' | 'CLOSED';
  customer?: User | null;
  booking?: Booking | null;
  updatedDate: string;
  createdDate: string;
}

export class AdditionalDriver {
  firstName = '';
  lastName = '';
  dateOfBirth: string | null = null;
  licenseNumber = '';
  licenseDate: string | null = null;
  licenseAuthority = '';
}

export interface LocationPoint {
  type: 'Point';
  coordinates: [number, number];
}

export interface BookingLocation {
  locationType: BookingLocationType;
  station: Station | null;
  location: GeocodedLocation | null;
}

export interface BookingCoordinates {
  longitude: number;
  latitude: number;
}

export interface LocationGeocodedName {
  displayName: string;
  country?: string;
  street?: string;
  zip?: string;
  city?: string;
}

export interface GeocodedLocation {
  address?: LocationGeocodedName | null;
  placeId?: string | null;
  coordinates: BookingCoordinates | null;
}

export interface CreateBookingDto {
  dryRun?: boolean;
  startDate: Date | string;
  endDate: Date | string;
  bookedCarId: string;
  pickupLocationType?: BookingLocationType;
  pickupStationId?: string | null;
  pickupLocation?: BookingCoordinates | null;
  pickupLocationGeocodedAddress?: LocationGeocodedName | null;
  dropoffLocationType?: BookingLocationType;
  dropoffStationId?: string | null;
  dropoffLocation?: BookingCoordinates | null;
  dropoffLocationGeocodedAddress?: LocationGeocodedName | null;
  customerId?: string | null;
  customerData?: CustomerDataDto;
  customerComment?: string | null;
  confirmationReply?: string | null;
  externalReference?: string | null;
  internalComment?: string | null;
  additionalDrivers: AdditionalDriver[] | null;
  relatedBookingIds?: string[];
  agencyId?: string | null;
  rentalPartner?: RentalPartner | null;
  insuranceCaseId?: string;
  status?: BookingStatus;
  taxProcedure?: TaxProcedure;
  pricing: BookingPricingDto;
}

export interface UpdateBookingDto extends Partial<CreateBookingDto> {
  status?: BookingStatus;
  additionalDrivers?: AdditionalDriver[];
  contractFileKey?: string;
  blockedCreditCardAmount?: number | null;
  blockedCreditCardDate?: string | null;
  blockedCreditCardIncludesOpenFee?: boolean | null;
  returnDate?: string | null;
  hasUnhandledDamages?: boolean;
  hasPeriodicInvoicing?: boolean;
  taxProcedure?: TaxProcedure;
  collectDeposit?: boolean;
  allocations?: BookingAllocationDto[];
  storedPaymentMethodId?: string;
  surchargeAmount?: number;
}

export interface UpdateBookingQueryDto {
  cancelCancellationFeeInvoice?: boolean;
  cancelledBy?: CancelledBy;
  handoverAdjustment?: boolean;
  overrideCancellationFee?: CancellationFee;
  overrideCancellationFeeComment?: string;
  sendCancelledMail?: boolean;
}

export interface FindOneBookingQueryDto {
  handoverAdjustment?: boolean;
}

export interface FindAllBookingsQueryDto extends PaginationParams<FindAllBookingsSort> {
  filter?: FindAllBookingsFilterDto[];
}

export interface FindAllBookingsFilterDto {
  bookingNumbers?: number[];
  pickupLocationType?: BookingLocationType;
  dropoffLocationType?: BookingLocationType;
  pickupStationIds?: string[] | null;
  dropoffStationIds?: string[] | null;
  startDateBefore?: string;
  startDateAfter?: string;
  endDateBefore?: string;
  endDateAfter?: string;
  createdDateBefore?: string;
  createdDateAfter?: string;
  status?: BookingStatus[];
  vehicleTypeIds?: string[];
  carIds?: string[];
  carStationIds?: string[];
  couponIds?: string[];
  customerIds?: string[];
  agencyIds?: string[];
  query?: string;
  hasTransfers?: boolean;
  hasUnhandledDamages?: boolean;
  hasPeriodicInvoicing?: boolean;
}
